import { useState, useId } from "react";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";
import {
  DrawerContent,
  DrawerFooter,
  DrawerTopBar,
  DrawerTopBarActionButton,
} from "@/components/Drawer";
import { PriceRangeSlider } from "@/components/Sliders/PriceRangeSlider";
import { BoardBasisFilter } from "@/features/hotels/components/hotels-list/BoardBasisFilter";
import HotelsListsSort from "@/features/hotels/components/hotels-list/HotelsListSort";
import { StarRatingFilter } from "@/features/hotels/components/hotels-list/StarRatingFilter";
import type {
  HotelsSortValue,
  IHotelsFilters,
} from "@/features/hotels/types/types";
import { BOARD_BASIS_OPTIONS } from "@/features/hotels/utils/constants";
import { initialSortValue } from "@/features/hotels/utils/initialHotelsValues";
import { useToggleSelectAll } from "@/hooks/useToggleSelectAll";
import { rudderAnalytics, HotelFiltersTrackingEvents } from "@/lib/rudderstack";

type Props = {
  defaultFilters: IHotelsFilters;
  filters: IHotelsFilters;
  bookingNights: number;
  sortValue: HotelsSortValue;
  onFiltersChange: (
    newFilters: Partial<IHotelsFilters>,
    newSort: HotelsSortValue,
  ) => void;
  onClose: () => void;
};

export function HotelsFilters({
  defaultFilters,
  filters,
  bookingNights,
  sortValue,
  onFiltersChange,
  onClose,
}: Props) {
  const priceHeaderId = useId();
  const intl = useIntl();
  const [localFilters, setLocalFilters] = useState<IHotelsFilters>({
    ...filters,
  });
  const [sort, setSort] = useState<HotelsSortValue>(sortValue);

  const {
    manuallySelectedAll: manuallySelectedAllBoardBasis,
    setManuallySelectedAll: setManuallySelectedAllBoardBasis,
    areAllSelected: areAllBoardBasisSelected,
    toggle: toggleBoardBasis,
  } = useToggleSelectAll<string>({
    selected: localFilters.boardBasis || [],
    all: BOARD_BASIS_OPTIONS,
  });

  const areBoardBasisInDefaultState =
    areAllBoardBasisSelected && !manuallySelectedAllBoardBasis;

  return (
    <>
      <DrawerTopBar
        onCloseClick={onClose}
        title={intl.formatMessage({
          defaultMessage: "Filters",
          description: "Heading for the hotel filters section",
        })}
        actionElement={
          <DrawerTopBarActionButton
            onClick={() => {
              setLocalFilters(defaultFilters);
              setSort(initialSortValue);
            }}
          >
            {intl.formatMessage({
              defaultMessage: "Clear filters",
              description: "Button to remove all active filters at once",
            })}
          </DrawerTopBarActionButton>
        }
      />

      <DrawerContent>
        <div className="flex flex-grow flex-col gap-5">
          <div>
            <h3 className="mb-2 text-lg font-bold text-text-primary">
              {intl.formatMessage({
                defaultMessage: "Sorting",
                description:
                  "Label for the sort options in the hotel filters panel",
              })}
            </h3>
            <HotelsListsSort value={sort} onChange={setSort} />
          </div>

          <div>
            <h3
              id={priceHeaderId}
              className="mb-4 text-lg font-bold text-text-primary"
            >
              {intl.formatMessage({
                defaultMessage: "Price Range (per night)",
                description:
                  "Filter label for the per-night price range slider",
              })}
            </h3>
            <PriceRangeSlider
              min={defaultFilters.priceRange[0]}
              max={defaultFilters.priceRange[1]}
              aria-labelledby={priceHeaderId}
              value={[localFilters.priceRange[0], localFilters.priceRange[1]]}
              scale={(value) => value / bookingNights}
              descale={(value) => Math.round(value * bookingNights)}
              onChange={(newValue) => {
                setLocalFilters((prevFilters) => ({
                  ...prevFilters,
                  priceRange: newValue,
                }));
              }}
            />
          </div>

          <div>
            <h3 className="mb-4 text-lg font-bold text-text-primary">
              {intl.formatMessage({
                defaultMessage: "Star rating",
                description:
                  "Filter section label for filtering by hotel star class",
              })}
            </h3>

            <StarRatingFilter
              selectedClasses={localFilters.starRating}
              onChange={(_e, checked, option) => {
                setLocalFilters((prevFilter) => {
                  const newClasses = checked
                    ? [...prevFilter.starRating, option]
                    : prevFilter.starRating.filter((c) => c !== option);
                  return {
                    ...prevFilter,
                    starRating: newClasses.sort((a, b) => a - b),
                  };
                });
              }}
            />
          </div>

          <div>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="shrink-0 text-lg font-bold text-text-primary">
                {intl.formatMessage({
                  defaultMessage: "Room options",
                  description:
                    "Filter section label for filtering by board basis / meal plan",
                })}
              </h3>
              <Button
                variant="textFit"
                onClick={(e) => {
                  if (areBoardBasisInDefaultState) {
                    setLocalFilters((prevFilters) => ({
                      ...prevFilters,
                      boardBasis: BOARD_BASIS_OPTIONS,
                    }));
                    setManuallySelectedAllBoardBasis(true);
                  } else {
                    setLocalFilters((prevFilters) => ({
                      ...prevFilters,
                      boardBasis: defaultFilters.boardBasis,
                    }));
                    setManuallySelectedAllBoardBasis(false);
                  }

                  // Remove focus from the button after click
                  e.currentTarget.blur();
                }}
              >
                {areBoardBasisInDefaultState
                  ? intl.formatMessage({
                      defaultMessage: "Select All",
                      description:
                        "Button to select all items in a list at once",
                    })
                  : intl.formatMessage({
                      defaultMessage: "Clear filter",
                      description: "Button to remove a single active filter",
                    })}
              </Button>
            </div>

            <BoardBasisFilter
              selected={localFilters.boardBasis}
              userManuallySelectedAll={manuallySelectedAllBoardBasis}
              onChange={(_e, checked, option) => {
                setLocalFilters((prevFilter) => {
                  const newClasses = toggleBoardBasis(
                    prevFilter.boardBasis || [],
                    option,
                    checked,
                  ).sort((a, b) => a.localeCompare(b));
                  return { ...prevFilter, boardBasis: newClasses };
                });
              }}
            />
          </div>
        </div>
      </DrawerContent>

      <DrawerFooter>
        <Button
          fullWidth
          onClick={() => {
            rudderAnalytics.track(
              HotelFiltersTrackingEvents.USER_CLICKED_ON_APPLY_ALL_FILTER,
              {
                sort_order: sort.field,
                price_range: {
                  min: Math.round(
                    localFilters.priceRange[0] / Math.max(1, bookingNights),
                  ),
                  max: Math.round(
                    localFilters.priceRange[1] / Math.max(1, bookingNights),
                  ),
                },
                stars_selected: localFilters.starRating,
                room_options: localFilters.boardBasis ?? [],
              },
            );
            onFiltersChange(localFilters, sort);
          }}
          variant="contained"
          size="large"
        >
          {intl.formatMessage({
            defaultMessage: "Apply",
            description: "Button to apply selected filters or changes",
          })}
        </Button>
      </DrawerFooter>
    </>
  );
}
